import { toCartAddress } from '@shared/shop-api/address/mapper'
import { toCartLine } from '@shared/shop-api/cart/lines/mapper'
import type { CartLine } from '@shared/shop-api/cart/lines/types'
import type { Promotion } from '@shared/shop-api/cart/types'
import { toCartCost } from '@shared/shop-api/cost/mapper'
import { toDelivery } from '@shared/shop-api/delivery/mapper'
import {
  toCartDiscount,
  toCartDiscountAllocation,
} from '@shared/shop-api/discount/mapper'
import { toError } from '@shared/shop-api/error/mapper'
import type { CartErrorFragment } from '@shared/shop-api/error/types'
import {
  isPromotedProductTriggerRuleAmount,
  isPromotedProductTriggerRuleQuantity,
} from '@shared/shop-api/product/attributes/check'
import { toWarning } from '@shared/shop-api/warning/mapper'
import type {
  CartFragment,
  CartWarningFragment,
} from '@shared/types/storefront/graphql'

export const toCart = (
  cart: CartFragment,
  warnings: CartWarningFragment[] = [],
  errors: CartErrorFragment[] = []
) => {
  const lines = cart.lines.items.map(toCartLine)
  const availablePromotions = toAvailablePromotions(lines)
  const mappedWarnings = warnings.map(toWarning)
  const discounts = cart.discountCodes.map(toCartDiscount)

  return {
    id: cart.id,
    buyerIdentity: {
      countryCode: cart.buyerIdentity.countryCode ?? undefined,
      email: cart.buyerIdentity.email ?? undefined,
      phone: cart.buyerIdentity.phone ?? undefined,
    },
    checkoutUrl: cart.checkoutUrl ?? undefined,
    cost: toCartCost(cart.cost),
    createdAt: new Date(cart.createdAt),
    delivery: {
      addresses: cart.delivery.addresses.map(toCartAddress),
      ...toDelivery(cart.deliveryGroups.items[0]),
    },
    lines,
    availablePromotions,
    discountWarning: mappedWarnings.find((warning) =>
      warning.code.startsWith('DISCOUNT_')
    ),
    merchandiseWarning: mappedWarnings.find((warning) =>
      warning.code.startsWith('MERCHANDISE_')
    ),
    errors: errors.map(toError),
    discounts,
    discountAllocations: cart.discountAllocations.map(toCartDiscountAllocation),
    quantity: cart.lines.items.reduce((sum, line) => sum + line.quantity, 0),
  }
}

export const toAvailablePromotions = (lines: CartLine[]) => {
  const linesWithPromotedProducts = lines.filter(
    (line) => !!line.variant.product?.attributes.promotedProducts?.length
  )
  const promotions = linesWithPromotedProducts
    .flatMap(toPromotions)
    .filter(
      (promotion, index, self) =>
        index ===
        self.findIndex(({ discountId }) => discountId === promotion.discountId)
    )

  return promotions.filter((promotion) => {
    if (!hasEnoughTriggerProductQuantity(lines, promotion)) {
      return false
    }

    if (!hasEnoughTriggerProductAmount(lines, promotion)) {
      return false
    }

    return isDiscountProductQuantityNotReached(lines, promotion)
  })
}

export const isDiscountProductQuantityNotReached = (
  lines: CartLine[],
  promotion: Promotion
) => {
  const requiredDiscountQuantity = promotion.discountRule.quantity

  const productQuantityById = new Map<number, number>()

  for (const line of lines) {
    const productId = line.variant.product.id
    const currentQuantity = productQuantityById.get(productId) ?? 0
    productQuantityById.set(productId, currentQuantity + line.quantity)
  }

  const discountProductsQuantity = promotion.items.reduce(
    (count, productId) => count + (productQuantityById.get(productId) ?? 0),
    0
  )

  return discountProductsQuantity < requiredDiscountQuantity
}

export const hasEnoughTriggerProductQuantity = (
  lines: CartLine[],
  promotion: Promotion
) => {
  const triggerQuantity = isPromotedProductTriggerRuleQuantity(
    promotion.triggerRule
  )
    ? promotion.triggerRule.quantity
    : 1

  const triggerProductsQuantity = lines.reduce((count, line) => {
    const { promotedProducts } = line.variant.product.attributes
    const hasCurrentPromotion = promotedProducts.some(
      (promotedProduct) => promotedProduct.discountId === promotion.discountId
    )

    if (hasCurrentPromotion) {
      return count + line.quantity
    }

    return count
  }, 0)

  return triggerProductsQuantity >= triggerQuantity
}

export const hasEnoughTriggerProductAmount = (
  lines: CartLine[],
  promotion: Promotion
) => {
  const triggerAmount = isPromotedProductTriggerRuleAmount(
    promotion.triggerRule
  )
    ? promotion.triggerRule.amount
    : 0

  const triggerProductsAmount = lines.reduce((amount, line) => {
    const { promotedProducts } = line.variant.product.attributes
    const hasCurrentPromotion = promotedProducts.some(
      (promotedProduct) => promotedProduct.discountId === promotion.discountId
    )

    if (hasCurrentPromotion) {
      return amount + line.cost.totalAmount
    }

    return amount
  }, 0)

  return triggerProductsAmount >= triggerAmount
}

export const toPromotions = (line: CartLine) => {
  return line.variant.product.attributes.promotedProducts.map(
    (promotedProduct) => ({
      promotedByLineId: line.id,
      ...promotedProduct,
    })
  )
}
